import {
  Alert,
  AlertDescription,
  AlertProps,
  Box,
  Flex,
  VisuallyHidden,
} from "@chakra-ui/react";
import equal from "fast-deep-equal";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import AffectedDependencies from "@/common/components/AffectedDependencies";
import HeaderLayout from "@/common/components/HeaderLayout";
import { MotionScaleFade } from "@/common/components/MotionComponents";
import { UpsertPopover } from "@/common/components/UpsertPopover";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFilterCheck } from "@/common/data/useFilterCheck";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { ActiveFeaturesAlert } from "@/company/components/ActiveFeaturesAlert";
import { CompaniesTable } from "@/company/components/CompaniesTable";
import { SegmentAudienceCountIndicator } from "@/company/components/SegmentAudienceCountIndicator";
import { SegmentNavigator } from "@/company/components/SegmentNavigator";
import { SegmentViewToggle } from "@/company/components/SegmentViewToggle";
import { UsersTableWithData } from "@/company/components/UsersTableWithData";
import { useSegments } from "@/company/data/useSegments";
import { useSegmentUpsertMutations } from "@/company/data/useSegmentUpsertMutations";
import { useCompaniesTableState } from "@/company/hooks/useCompaniesTableState";
import { useFilterParam } from "@/company/hooks/useFilterParam";
import { useSegmentAudienceCount } from "@/company/hooks/useSegmentAudienceCount";
import { useSegmentRouteParam } from "@/company/hooks/useSegmentRouteParam";
import { useUsersTableState } from "@/company/hooks/useUsersTableState";
import { SegmentDTO } from "@reflag/shared/segmentAPI";
import { SegmentUrl } from "@reflag/shared/urls";

const ConditionalAlert = ({ children, ...rest }: AlertProps) =>
  children ? (
    <Alert status="info" {...rest}>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  ) : null;

function Page({ segment }: { segment: SegmentDTO }) {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  const { viewType, setViewType } = useSegmentRouteParam();

  const [filter, setFilter] = useFilterParam(segment.companyFilter);
  const filterChanged = !equal(filter, segment.companyFilter);

  const companyTableState = useCompaniesTableState(segment);
  const usersTableState = useUsersTableState(segment);
  const audienceCount = useSegmentAudienceCount({
    viewType,
    filter,
    companySearchQuery: companyTableState.searchQuery,
    userSearchQuery: usersTableState.searchQuery,
  });

  // todo: improve with issue BUC-2889
  const dependee = { type: "segment", id: segment.id } as const;
  const { data: filterCheck } = useFilterCheck(
    filterChanged ? dependee : undefined,
    filter,
  );
  const canUpdateSegment = useMemo(
    () =>
      !filterChanged ||
      (filterCheck &&
        !filterCheck.cycle &&
        (!filterCheck.features.length || filterCheck.stateless)),
    [filterChanged, filterCheck],
  );

  const segmentUpsert = useSegmentUpsertMutations(segment, filter, {
    onSuccess: (segment, { action }) => {
      if (action == "create") navigate(SegmentUrl(env, segment));
    },
  });

  // usersTableState.isDirty is always false for now
  // we don't store user table state yet
  const isDirty = useMemo(() => {
    return (
      filterChanged || companyTableState.isDirty || usersTableState.isDirty
    );
  }, [filterChanged, companyTableState.isDirty, usersTableState.isDirty]);

  return (
    <>
      <VisuallyHidden>
        <h1>{segment?.name}</h1>
      </VisuallyHidden>

      <Helmet>
        <title>Segments › {segment?.name}</title>
      </Helmet>

      <HeaderLayout
        actions={
          <>
            <Flex align="center" gap={3}>
              <AnimatePresence>
                {isDirty && (
                  <MotionScaleFade origin="bottom">
                    <Restricted role="publisher">
                      <UpsertPopover
                        canCreate={!filterCheck?.cycle}
                        canUpdate={canUpdateSegment}
                        entity="Segment"
                        mutation={segmentUpsert}
                        onReset={() => {
                          setFilter(undefined);

                          // reset the columns
                          companyTableState.onReset();
                          usersTableState.onReset();
                        }}
                      >
                        {(action) => (
                          <>
                            {filterChanged && (
                              <>
                                <AffectedDependencies
                                  changeType="update"
                                  dependee={dependee}
                                  filter={filter}
                                />
                                <ActiveFeaturesAlert
                                  dependee={dependee}
                                  filter={filter}
                                />
                              </>
                            )}
                            <ConditionalAlert>
                              {action === "update" &&
                              segment.isAllSegment &&
                              filterChanged
                                ? `Filters can't be changed on the "${segment.name}" segment. Only sorting and column configuration will be saved.`
                                : null}
                            </ConditionalAlert>
                          </>
                        )}
                      </UpsertPopover>
                    </Restricted>
                  </MotionScaleFade>
                )}
              </AnimatePresence>
            </Flex>
          </>
        }
        title={
          <>
            <Box ml={-2}>
              <SegmentNavigator currentSegment={segment} viewType={viewType} />
            </Box>
            <SegmentAudienceCountIndicator
              audienceCount={audienceCount}
              viewType={viewType}
            />
          </>
        }
        borderless
      >
        {viewType === "companies" ? (
          <CompaniesTable
            segment={segment}
            audienceCount={audienceCount}
            filter={filter}
            tableState={companyTableState}
            toolbarRightActions={
              <SegmentViewToggle
                viewType={"companies"}
                onViewTypeChange={setViewType}
              />
            }
            onFilterChange={setFilter}
          />
        ) : (
          <UsersTableWithData
            segment={segment}
            audienceCount={audienceCount}
            filter={filter}
            tableState={usersTableState}
            toolbarRightActions={
              <SegmentViewToggle
                viewType={"users"}
                onViewTypeChange={setViewType}
              />
            }
            onFilterChange={setFilter}
          />
        )}
      </HeaderLayout>
    </>
  );
}

export default function Segments() {
  const { segmentId } = useSegmentRouteParam();
  const { data: segments, isSuccess } = useSegments();
  const errorToast = useErrorToast();
  const navigate = useNavigate();
  const { search, hash } = useLocation();
  const { env } = useEnvironment();

  const allSegment = segments?.find((s) => s.isAllSegment);
  const currentSegment = segments?.find((s) => s.id === segmentId);

  useEffect(() => {
    if (!segmentId && allSegment) {
      // no segment id, redirect to all segment
      navigate(`${SegmentUrl(env, allSegment)}${search}${hash}`, {
        replace: true,
      });
    }

    if (segmentId && isSuccess && !currentSegment) {
      errorToast({
        title: `Segment not found`,
        description: `The segment "${segmentId}" does not exist. Redirecting to All segment.`,
        status: "error",
      });

      if (allSegment) {
        navigate(`${SegmentUrl(env, allSegment)}${search}${hash}`, {
          replace: true,
        });
      }
    }
  }, [
    segmentId,
    isSuccess,
    currentSegment,
    errorToast,
    allSegment,
    navigate,
    env,
    search,
    hash,
  ]);

  if (!currentSegment) return null;

  return <Page key={currentSegment.id} segment={currentSegment} />;
}
